import { useState } from 'react';
import { MagnifyingGlass, CheckCircle, Plus, PencilSimple, Trash, Globe, ArrowSquareOut, Copy, Check, BookmarkSimple, EnvelopeSimple, PaperPlaneTilt, FloppyDisk, WhatsappLogo } from '@phosphor-icons/react';
import axios from 'axios';

const API_URL = (typeof window !== 'undefined' && window.location.hostname && !/^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname)) ? window.location.origin : process.env.REACT_APP_BACKEND_URL;

const ACTION_ICONS = {
  create: { icon: Plus, color: 'text-[#10b981]', bg: 'bg-[#d1fae5]', label: 'Task created' },
  complete: { icon: CheckCircle, color: 'text-[#0ea5e9]', bg: 'bg-[#e0f2fe]', label: 'Task completed' },
  update: { icon: PencilSimple, color: 'text-[#f59e0b]', bg: 'bg-[#fef3c7]', label: 'Task updated' },
  delete: { icon: Trash, color: 'text-[#ef4444]', bg: 'bg-[#fee2e2]', label: 'Task deleted' },
  draft_email: { icon: EnvelopeSimple, color: 'text-[#7c3aed]', bg: 'bg-[#ede9fe]', label: 'Email drafted' },
  whatsapp_send: { icon: WhatsappLogo, color: 'text-[#25d366]', bg: 'bg-[#dcfce7]', label: 'WhatsApp sent' },
  whatsapp_read: { icon: WhatsappLogo, color: 'text-[#25d366]', bg: 'bg-[#dcfce7]', label: 'WhatsApp chat read' },
};

const cleanDomain = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

export const ChatMessage = ({ message, savedSourceUrls = [], onSourceSaved }) => {
  const isUser = message.role === 'user';
  const actions = message.actions || [];
  const citations = message.citations || [];
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4 group/msg`} data-testid={`chat-msg-${message.role}`}>
      <div className={`max-w-[85%] sm:max-w-[70%] ${isUser ? 'order-2' : 'order-1'}`}>
        {/* Search indicators */}
        <div className="flex items-center gap-3 mb-1 px-1">
          {message.gmail_searched && (
            <div className="flex items-center gap-1 text-[10px] text-[#7c3aed] font-medium">
              <MagnifyingGlass size={10} weight="bold" />
              Emails: {message.gmail_query}
            </div>
          )}
          {message.web_searched && (
            <div className="flex items-center gap-1 text-[10px] text-[#0ea5e9] font-medium">
              <Globe size={10} weight="bold" />
              Web: {message.web_query}
            </div>
          )}
        </div>

        {/* Message bubble */}
        <div className="relative">
          <div
            className={`rounded-2xl px-4 py-3 text-sm leading-relaxed ${
              isUser
                ? 'bg-[#0f172a] text-white rounded-br-md'
                : 'bg-[#f2f4f6] text-[#2d3338] rounded-bl-md'
            }`}
          >
            <MessageContent text={message.content} isUser={isUser} citations={citations} />
          </div>

          {/* Copy button — assistant messages only */}
          {!isUser && (
            <button
              onClick={handleCopy}
              className="absolute -bottom-1 right-2 opacity-0 group-hover/msg:opacity-100 transition-opacity bg-white border border-[#e5e7eb] rounded-md p-1 shadow-sm hover:bg-[#f8f9fa]"
              data-testid="copy-message-btn"
              title="Copy message"
            >
              {copied ? (
                <Check size={13} weight="bold" className="text-[#10b981]" />
              ) : (
                <Copy size={13} weight="bold" className="text-[#596065]" />
              )}
            </button>
          )}
        </div>

        {/* Citations — SOURCES section */}
        {citations.length > 0 && (
          <div className="mt-2.5 px-1" data-testid="citations-section">
            <p className="text-[10px] font-semibold text-[#596065] mb-1.5 uppercase tracking-wider">Sources</p>
            <div className="flex flex-wrap gap-1.5">
              {citations.map((url, i) => {
                const domain = cleanDomain(url);
                const isSaved = savedSourceUrls.includes(url);
                return (
                  <CitationChip
                    key={i}
                    url={url}
                    domain={domain}
                    index={i + 1}
                    isSaved={isSaved}
                    onSourceSaved={onSourceSaved}
                  />
                );
              })}
            </div>
          </div>
        )}

        <p className={`text-[10px] text-[#acb3b8] mt-1 px-1 ${isUser ? 'text-right' : ''}`}>
          {message.ts ? formatTime(message.ts) : ''}
        </p>

        {/* Action confirmations */}
        {actions.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {actions.map((a, i) => {
              if (a.action === 'draft_email' && a.success) {
                return <EmailDraftCard key={i} draft={a} />;
              }
              const config = ACTION_ICONS[a.action] || ACTION_ICONS.create;
              const Icon = config.icon;
              return (
                <div key={i} className={`flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg ${config.bg} ${config.color}`} data-testid={`action-badge-${a.action}`}>
                  <Icon size={12} weight="bold" />
                  <span className="font-medium">{config.label}: {a.title || 'Done'}</span>
                  {!a.success && <span className="text-[#ef4444]"> (failed)</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const CitationChip = ({ url, domain, index, isSaved, onSourceSaved }) => {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(isSaved);

  const handleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (saved || saving) return;
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/assistant/sources/save`, { url, domain, title: domain }, { withCredentials: true });
      setSaved(true);
      onSourceSaved?.(url);
    } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <div className="inline-flex items-center rounded-lg bg-white border border-[#e5e7eb] hover:border-[#0ea5e9] transition-all group/chip">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] text-[#374151] hover:text-[#0369a1] transition-colors"
        data-testid={`citation-link-${index}`}
      >
        <span className="flex items-center justify-center w-4 h-4 rounded-full bg-[#0ea5e9] text-white text-[9px] font-bold flex-shrink-0">
          {index}
        </span>
        <img
          src={`https://www.google.com/s2/favicons?domain=${domain}&sz=16`}
          alt=""
          className="w-3.5 h-3.5 rounded-sm flex-shrink-0"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <span className="truncate max-w-[140px]">{domain}</span>
        <ArrowSquareOut size={10} className="text-[#acb3b8] group-hover/chip:text-[#0ea5e9] flex-shrink-0" />
      </a>
      <button
        onClick={handleSave}
        className={`px-1.5 py-1 border-l border-[#e5e7eb] transition-colors ${
          saved ? 'text-[#f59e0b]' : 'text-[#acb3b8] hover:text-[#f59e0b]'
        }`}
        title={saved ? 'Saved' : 'Save source'}
        data-testid={`save-source-${index}`}
      >
        <BookmarkSimple size={12} weight={saved ? 'fill' : 'bold'} />
      </button>
    </div>
  );
};

const EmailDraftCard = ({ draft }) => {
  const [status, setStatus] = useState('pending_review');
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [to, setTo] = useState(draft.to || '');
  const [subject, setSubject] = useState(draft.subject || '');
  const [body, setBody] = useState((draft.body || '').replace(/\\n/g, '\n'));

  const updateDraftOnServer = async () => {
    try {
      await axios.post(`${API_URL}/api/assistant/email/update-draft`, {
        draft_id: draft.draft_id, to, subject, body
      }, { withCredentials: true });
    } catch { /* best effort */ }
  };

  const handleSaveDraft = async () => {
    setStatus('saving');
    await updateDraftOnServer();
    try {
      const res = await axios.post(`${API_URL}/api/assistant/email/save-draft`, { draft_id: draft.draft_id }, { withCredentials: true });
      if (res.data.saved) { setStatus('saved'); setEditing(false); }
      else { setStatus('error'); setError(res.data.error || 'Failed to save'); }
    } catch { setStatus('error'); setError('Network error'); }
  };

  const handleSend = async () => {
    setStatus('sending');
    await updateDraftOnServer();
    try {
      const res = await axios.post(`${API_URL}/api/assistant/email/send`, { draft_id: draft.draft_id }, { withCredentials: true });
      if (res.data.sent) { setStatus('sent'); setEditing(false); }
      else { setStatus('error'); setError(res.data.error || 'Failed to send'); }
    } catch { setStatus('error'); setError('Network error'); }
  };

  const isEditable = status === 'pending_review';

  return (
    <div className="mt-2 rounded-xl border border-[#e0d4f5] bg-[#faf8ff] overflow-hidden" data-testid="email-draft-card">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-[#ede9fe] border-b border-[#e0d4f5]">
        <EnvelopeSimple size={14} className="text-[#7c3aed]" weight="duotone" />
        <span className="text-[11px] font-bold text-[#7c3aed] uppercase tracking-wider">Email Draft</span>
        {isEditable && !editing && (
          <button onClick={() => setEditing(true)} className="ml-auto text-[10px] font-medium text-[#7c3aed] hover:underline" data-testid="edit-draft-btn">
            <PencilSimple size={12} weight="bold" className="inline mr-0.5" />Edit
          </button>
        )}
        {editing && (
          <button onClick={() => setEditing(false)} className="ml-auto text-[10px] font-medium text-[#596065] hover:underline">Done editing</button>
        )}
        {status === 'sent' && <span className="ml-auto text-[10px] font-bold text-[#10b981]">Sent</span>}
        {status === 'saved' && <span className="ml-auto text-[10px] font-bold text-[#0ea5e9]">Saved to Drafts</span>}
      </div>

      {/* Email fields */}
      <div className="px-3 py-2.5 space-y-1.5">
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-[#7c7c8a] font-medium w-8 flex-shrink-0">To:</span>
          {editing ? (
            <input value={to} onChange={e => setTo(e.target.value)}
              className="flex-1 text-[11px] font-semibold text-[#2d3338] bg-white border border-[#d0d4f5] rounded px-2 py-1 outline-none focus:border-[#7c3aed]"
              data-testid="draft-to-input" />
          ) : (
            <span className="text-[#2d3338] font-semibold">{to}</span>
          )}
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          <span className="text-[#7c7c8a] font-medium w-8 flex-shrink-0">Subj:</span>
          {editing ? (
            <input value={subject} onChange={e => setSubject(e.target.value)}
              className="flex-1 text-[11px] font-semibold text-[#2d3338] bg-white border border-[#d0d4f5] rounded px-2 py-1 outline-none focus:border-[#7c3aed]"
              data-testid="draft-subject-input" />
          ) : (
            <span className="text-[#2d3338] font-semibold">{subject}</span>
          )}
        </div>
        {editing ? (
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={6}
            className="w-full mt-2 text-[11px] text-[#4a4a5a] bg-white rounded-lg p-2.5 border border-[#d0d4f5] leading-relaxed outline-none focus:border-[#7c3aed] resize-y"
            data-testid="draft-body-input" />
        ) : (
          <div className="mt-2 text-[11px] text-[#4a4a5a] bg-white rounded-lg p-2.5 border border-[#f0f0f5] whitespace-pre-wrap leading-relaxed max-h-[200px] overflow-y-auto cursor-pointer hover:border-[#d0d4f5] transition-colors"
            onClick={() => isEditable && setEditing(true)} title={isEditable ? "Click to edit" : ""}>
            {body}
          </div>
        )}
      </div>

      {/* Actions */}
      {isEditable && (
        <div className="flex items-center gap-2 px-3 py-2 border-t border-[#e0d4f5] bg-[#f8f6ff]">
          <button onClick={handleSaveDraft}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-white border border-[#d0d4f5] text-[#7c3aed] hover:bg-[#ede9fe] transition-colors"
            data-testid="save-draft-btn">
            <FloppyDisk size={12} weight="bold" /> Save to Drafts
          </button>
          <button onClick={handleSend}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[10px] font-semibold bg-[#7c3aed] text-white hover:bg-[#6d28d9] transition-colors"
            data-testid="send-email-btn">
            <PaperPlaneTilt size={12} weight="bold" /> Send Now
          </button>
          <span className="ml-auto text-[9px] text-[#acb3b8]">{editing ? 'Editing...' : 'Click text to edit'}</span>
        </div>
      )}
      {(status === 'saving' || status === 'sending') && (
        <div className="flex items-center gap-2 px-3 py-2 border-t border-[#e0d4f5]">
          <div className="w-3 h-3 border-2 border-[#7c3aed] border-t-transparent rounded-full animate-spin" />
          <span className="text-[10px] text-[#7c3aed]">{status === 'saving' ? 'Saving to Gmail Drafts...' : 'Sending...'}</span>
        </div>
      )}
      {status === 'error' && (
        <div className="flex items-center gap-2 px-3 py-2 border-t border-[#fecaca] bg-[#fef2f2]">
          <span className="text-[10px] text-[#dc2626] flex-1">{error}</span>
          {error.includes('reconnect') ? (
            <a href="/connections" className="text-[10px] font-semibold text-[#7c3aed] hover:underline whitespace-nowrap">Reconnect Gmail</a>
          ) : (
            <button onClick={() => setStatus('pending_review')} className="text-[10px] text-[#dc2626] underline whitespace-nowrap">Retry</button>
          )}
        </div>
      )}
    </div>
  );
};



const MessageContent = ({ text, isUser, citations = [] }) => {
  if (isUser) return <span>{text}</span>;

  // Strip citation blocks that Claude echoes — we render these separately in SOURCES
  let cleanText = text;
  // Remove "CITATIONS:" block with numbered URL lines (handles blank lines, various formats)
  cleanText = cleanText.replace(/\n*(?:CITATIONS|Citations|Sources|References):?\s*\n(?:\s*(?:\[\d+\]|\d+[\.\):])\s*(?:https?:\/\/)?[^\n]+\s*\n?)+/gi, '').trim();
  // Remove standalone trailing [n] URL lines (2+ in a row)
  cleanText = cleanText.replace(/\n*(?:\s*\[\d+\]\s*https?:\/\/[^\n]+\s*\n?){2,}$/i, '').trim();
  // Remove orphaned "Citations:" header
  cleanText = cleanText.replace(/\n*(?:CITATIONS|Citations|Sources|References):?\s*$/gim, '').trim();

  const lines = cleanText.split('\n');
  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-1" />;
        if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
          return (
            <div key={i} className="flex gap-2 pl-1">
              <span className="text-[#f59e0b] mt-0.5 flex-shrink-0">&#8226;</span>
              <span>{parseInline(line.trim().slice(2), citations)}</span>
            </div>
          );
        }
        return <p key={i}>{parseInline(line, citations)}</p>;
      })}
    </div>
  );
};

const parseInline = (text, citations = []) => {
  const parts = text.split(/(\*\*.*?\*\*|\[\d+\]|https?:\/\/[^\s),]+)/g);
  return parts.map((part, i) => {
    if (!part) return null;
    // Bold
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
    }
    // Citation reference [1], [2] — render as inline domain pill
    if (/^\[\d+\]$/.test(part)) {
      const num = parseInt(part.slice(1, -1), 10);
      const url = citations[num - 1];
      if (url) {
        const domain = cleanDomain(url);
        return (
          <a
            key={i}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-1.5 py-0 rounded-md bg-[#eef6ff] border border-[#d0e4f7] text-[10px] text-[#0369a1] hover:bg-[#dbeafe] hover:border-[#93c5fd] transition-colors no-underline mx-0.5 align-baseline"
            title={url}
            data-testid={`inline-citation-${num}`}
          >
            <img
              src={`https://www.google.com/s2/favicons?domain=${domain}&sz=16`}
              alt=""
              className="w-3 h-3 rounded-sm"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className="truncate max-w-[100px]">{domain}</span>
          </a>
        );
      }
      // No matching URL — hide the orphaned reference
      return <span key={i}></span>;
    }
    // Standalone URL — show as clean domain link
    if (/^https?:\/\//.test(part)) {
      const domain = cleanDomain(part);
      return (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="text-[#2563eb] hover:underline text-xs">
          {domain}
        </a>
      );
    }
    return part;
  });
};

const formatTime = (ts) => {
  try {
    const d = new Date(ts);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
};
